"use client";

import * as React from "react";

import { IntlProvider as ReactIntlProvider } from "react-intl";

import { ACTIVE_LOCALES } from "./i18n.consts";
import { Locale, LocaleData } from "./i18n.types";
import { handleIntlError, loadTranslationData } from "./load-translation-data";

type IntlProviderProps = {
  locale: Locale;
  children: React.ReactNode;
};

const LocaleDataContext = React.createContext<LocaleData | undefined>(
  undefined,
);

/**
 * Wraps react-intl's IntlProvider and updates messages on locale change.
 * Provides the current locale data to the LocaleDataContext.
 */
const IntlProvider = ({ locale, children }: IntlProviderProps) => {
  const messages = React.useMemo(() => {
    return loadTranslationData(locale);
  }, [locale]);

  const currentLocaleData = React.useMemo(() => {
    const currentLocale = ACTIVE_LOCALES[locale];
    return currentLocale;
  }, [locale]);

  return (
    <ReactIntlProvider
      locale={locale || "en-US"}
      messages={messages}
      onError={handleIntlError}
    >
      <LocaleDataContext.Provider value={currentLocaleData}>
        {children}
      </LocaleDataContext.Provider>
    </ReactIntlProvider>
  );
};

const useLocaleData = () => {
  const context = React.useContext(LocaleDataContext);
  if (!context) {
    throw new Error("useLocaleData must be used within an IntlProvider");
  }
  return context;
};

export { IntlProvider, useLocaleData };
